import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Download, Eye, Trash, Upload } from "lucide-react";
import { api, API, apiError, fmtDate } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

const kb = (n) => (!n ? "-" : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function DocumentsPanel({ recordType, recordId, onError, compact = false }) {
  const [data, setData] = useState(null);
  const [docType, setDocType] = useState("Other");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(`/documents/${recordType}/${recordId}`); setData(d); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); onError?.(); }
  }, [recordType, recordId, onError]);
  useEffect(() => { load(); }, [load]);

  const url = (id) => `${API}/files/download/${id}?token=${localStorage.getItem("token")}`;

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await api.post(
          `/files/upload?owner_module=${data?.module || "inventory"}&kind=document&record_type=${recordType}&record_id=${recordId}&doc_type=${encodeURIComponent(docType)}`,
          fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post(`/documents/${recordType}/${recordId}/attach`, { file_id: up.id, doc_type: docType });
      }
      toast.success(`${files.length} document(s) added`);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setBusy(false);
    e.target.value = "";
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this document from this record? The file itself is not deleted.")) return;
    try { const { data: r } = await api.delete(`/documents/${recordType}/${recordId}/${id}`); setData({ ...data, documents: r.documents }); toast.success("Removed"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="documents-panel">
        {data?.can_upload && (
          <div className="flex flex-wrap items-end gap-2 border border-slate-200 rounded-sm p-3 bg-slate-50/60">
            <div>
              <span className="text-xs text-slate-500">Document Type</span>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="doc-type-select" className="h-9 w-56 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>{(data.doc_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <input ref={fileRef} data-testid="doc-file-input" type="file" accept=".jpg,.jpeg,.png,.pdf" multiple className="hidden" onChange={onUpload} />
            <Button data-testid="doc-upload-btn" disabled={busy} className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5 h-9" onClick={() => fileRef.current?.click()}>
              <Upload className="w-4 h-4" /> {busy ? "Uploading..." : "Upload Document"}
            </Button>
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              {["Document Name", "Type", ...(compact ? [] : ["Uploaded By"]), "Upload Date", "Format", ...(compact ? [] : ["Size"]), ""].map((h) => (
                <th key={h} className="px-3 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}
            </tr></thead>
            <tbody>
              {!data ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Loading...</td></tr>
                : !data.documents.length ? <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400" data-testid="documents-empty">No documents attached yet</td></tr>
                  : data.documents.map((d) => (
                    <tr key={d.id} data-testid={`document-${d.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-800 max-w-[240px] truncate">{d.name}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="rounded-full px-2 text-[11px]">{d.doc_type}</Badge></td>
                      {!compact && <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{d.uploaded_by}</td>}
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{fmtDate(d.upload_date)}</td>
                      <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{d.format}</td>
                      {!compact && <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{kb(d.size)}</td>}
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Button data-testid={`doc-view-${d.id}`} size="icon" variant="ghost" className="h-8 w-8" title="View" onClick={() => window.open(url(d.id), "_blank")}><Eye className="w-4 h-4" /></Button>
                        <a data-testid={`doc-download-${d.id}`} href={url(d.id)} download={d.name} className="inline-flex items-center justify-center h-8 w-8 text-slate-500 hover:text-[#0B132B]" title="Download"><Download className="w-4 h-4" /></a>
                        {data.can_upload && <Button data-testid={`doc-remove-${d.id}`} size="icon" variant="ghost" className="h-8 w-8 text-red-600" title="Remove from this record" onClick={() => remove(d.id)}><Trash className="w-4 h-4" /></Button>}
                      </td>
                    </tr>))}
            </tbody>
          </table>
        </div>
    </div>
  );
}

export default function DocumentsDialog({ recordType, recordId, title, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="documents-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><FileText className="w-5 h-5" /> Documents {title ? `· ${title}` : ""}</DialogTitle>
          <DialogDescription>All files attached to this record. PDF, JPG, JPEG and PNG are supported.</DialogDescription>
        </DialogHeader>
        <DocumentsPanel recordType={recordType} recordId={recordId} onError={onClose} />
        <DialogFooter><Button variant="outline" className="rounded-sm" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
